import React, { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { SignUpProps } from "../../dtos/signUp/SignUpDto";
import { signUp } from "../../service/signUp/SignUpServices";
import { isTenantAvailable, isTenantNameAvailable } from "../../service/tenant/TenantService";
import { useDispatch } from "react-redux";
import { setTenancyName } from "../../store/slices/tenantSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { setSignupDetails } from "../../store/slices/signUpSlice";

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState<SignUpProps>({
    tenantName: "",
    tenancyName: "",
    name: "",
    surname: "",
    email: "",
    userName: "",
    password: "",
    phoneNumber: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  const [tenantNameAvailable, setTenantNameAvailable] = useState(true);
  const [tenancyNameAvailable, setTenancyNameAvailable] = useState(true);
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = e.target.name;
    const value = e.target.value;

    const updatedValue =
      ["tenantName", "tenancyName"].includes(fieldName)
        ? value.trim()
        : value;

    setFormData((prev) => ({
      ...prev,
      [fieldName]: updatedValue,
    }));

    // Clear validation error when user types
    if (validationErrors[fieldName]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }

    // Optimistically mark as "available" again until the new check resolves,
    // so a stale "taken" flag doesn't block submit after the user edits the field
    if (fieldName === "tenantName") {
      setTenantNameAvailable(true);
    } else if (fieldName === "tenancyName") {
      setTenancyNameAvailable(true);
    }

    // Run availability check only for Company Name & Company Display Name
    if (fieldName === "tenantName" || fieldName === "tenancyName") {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Empty value: nothing to check, just clear pending state
      if (!updatedValue) {
        setCheckingAvailability(false);
        return;
      }

      setCheckingAvailability(true);

      debounceTimerRef.current = setTimeout(async () => {
        try {
          if (fieldName === "tenancyName") {
            const response = await isTenantAvailable({
              tenancyName: updatedValue,
            });

            const taken = !!response?.data?.Result;
            setTenancyNameAvailable(!taken);

            if (taken) {
              setValidationErrors((prev) => ({
                ...prev,
                tenancyName: "This company display name is already taken.",
              }));
            }
          } else {
            const response = await isTenantNameAvailable({
              tenantName: updatedValue,
            });

            const taken = !!response?.data?.Result;
            setTenantNameAvailable(!taken);

            if (taken) {
              setValidationErrors((prev) => ({
                ...prev,
                tenantName: "This company name is already taken.",
              }));
            }
          }
        } catch (error) {
          console.error("Availability check failed:", error);
        } finally {
          setCheckingAvailability(false);
        }
      }, 1100);
    }
  };
  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(e.target.value);

    // Clear validation error when user types
    if (validationErrors.confirmPassword) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.confirmPassword;
        return newErrors;
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.tenantName.trim())
      errors.tenantName = "Company name is required";
    if (!formData.tenancyName.trim())
      errors.tenancyName = "Company display name is required";
    if (!formData.name.trim()) errors.name = "First name is required";
    if (!formData.surname.trim()) errors.surname = "Last name is required";

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    if (!formData.userName.trim()) errors.userName = "Username is required";

    if (!formData.password || !formData.password.trim()) {
      errors.password = "Password cannot be empty or spaces only";
    } else {
      const passwordRulesFailed =
        formData.password.length < 8 ||
        !/[A-Z]/.test(formData.password) ||
        !/[a-z]/.test(formData.password) ||
        !/[0-9]/.test(formData.password) ||
        !/[!@#$%^&*(),.?":{}|<>]/.test(formData.password);

      if (passwordRulesFailed) {
        errors.password =
          "Password must be 8+ chars with A-Z, a-z, 0-9, and a symbol."

        // "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.";
      }
    }
    // Confirm password validation
    if (!confirmPassword || !confirmPassword.trim()) {
      errors.confirmPassword = "Confirm password cannot be empty or spaces only";
    } else if (formData.password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    // Phone number is now optional, only validate format if provided
    if (formData.phoneNumber) {
      const digitsOnly = formData.phoneNumber.replace(/\D/g, "");

      if (digitsOnly.length < 10 || digitsOnly.length > 15) {
        errors.phoneNumber =
          "Phone number must be between 10 and 15 digits";
      }
    }


    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (checkingAvailability) return;
    if (!validateForm()) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await signUp(formData);
      dispatch(setTenancyName({ tenancyName: formData.tenancyName }));
      dispatch(setSignupDetails({ email: formData.email, password: formData.password }));

      if (response?.data?.Result) {
        // Successful sign up - redirect to login or confirmation page
        navigate("/login", {
          state: { message: "Account created successfully. Please log in." },
        });
      } else {
        // API returned error message
        setError(response?.data?.Error || response.result || "An error occurred during sign up");
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again later.");
      console.error("Sign up error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Import Inter font via Tailwind */}
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
      </style>
      {/* Custom animations are kept in the global CSS file */}

      {/* Loading spinner */}
      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="w-12 h-12 border-4 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
        </div>
      )}

      <div className="min-h-screen flex items-center justify-center p-5 bg-gradient-to-br from-slate-400 via-slate-500 to-slate-700 font-['Inter',sans-serif] relative overflow-x-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(220,197,146,0.2)_0%,transparent_50%),radial-gradient(circle_at_80%_20%,rgba(201,168,118,0.2)_0%,transparent_50%),radial-gradient(circle_at_40%_40%,rgba(184,149,106,0.1)_0%,transparent_50%)] z-0"></div>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden max-w-[1180px] w-full min-h-[650px] flex relative z-10 backdrop-blur-md animate-[containerFadeIn_0.8s_ease-out] lg:flex-row flex-col">
          {/* Left Side - Brand Content */}
          <div className="flex-1 bg-gradient-to-br from-slate-700 to-slate-800 text-white lg:p-20 md:p-10 p-10 flex flex-col justify-center items-center text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-40 z-[1]" style={{
              background: "url('data:image/svg+xml,%3Csvg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"%3E%3Cdefs%3E%3Cpattern id=\"dots\" width=\"20\" height=\"20\" patternUnits=\"userSpaceOnUse\"%3E%3Ccircle cx=\"10\" cy=\"10\" r=\"1\" fill=\"rgba(255,255,255,0.1)\"%3E%3C/circle%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\"100\" height=\"100\" fill=\"url(%23dots)\"%3E%3C/rect%3E%3C/svg%3E')",
              animation: "floatPattern 20s ease-in-out infinite"
            }}></div>

            <div className="relative z-[2]" style={{ animation: "slideInLeft 0.8s ease-out 0.2s both" }}>
              <div className="w-[60px] md:w-[90px] h-[60px] md:h-[90px] bg-gradient-to-br from-slate-600 to-slate-400 md:rounded-3xl rounded-xl flex items-center justify-center mx-auto mb-9 shadow-lg relative overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] hover:translate-y-[-5px] hover:scale-105 hover:shadow-xl">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 384 512"
                  fill="white"
                  className="relative z-[1] w-[30px] md:w-[42px] h-[30px] md:h-[42px]"
                >
                  <path d="M64 0C28.7 0 0 28.7 0 64L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-288-128 0c-17.7 0-32-14.3-32-32L224 0 64 0zM256 0l0 128 128 0L256 0zM64 80c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s-7.2 16-16 16L80 96c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16l64 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16zm128 72c8.8 0 16 7.2 16 16l0 17.3c8.5 1.2 16.7 3.1 24.1 5.1c8.5 2.3 13.6 11 11.3 19.6s-11 13.6-19.6 11.3c-11.1-3-22-5.2-32.1-5.3c-8.4-.1-17.4 1.8-23.6 5.5c-5.7 3.4-8.1 7.3-8.1 12.8c0 3.7 1.3 6.5 7.3 10.1c6.9 4.1 16.6 7.1 29.2 10.9l.5 .1s0 0 0 0s0 0 0 0c11.3 3.4 25.3 7.6 36.3 14.6c12.1 7.6 22.4 19.7 22.7 38.2c.3 19.3-9.6 33.3-22.9 41.6c-7.7 4.8-16.4 7.6-25.1 9.1l0 17.1c0 8.8-7.2 16-16 16s-16-7.2-16-16l0-17.8c-11.2-2.1-21.7-5.7-30.9-8.9c0 0 0 0 0 0c-2.1-.7-4.2-1.4-6.2-2.1c-8.4-2.8-12.9-11.9-10.1-20.2s11.9-12.9 20.2-10.1c2.5 .8 4.8 1.6 7.1 2.4c0 0 0 0 0 0s0 0 0 0s0 0 0 0c13.6 4.6 24.6 8.4 36.3 8.7c9.1 .3 17.9-1.7 23.7-5.3c5.1-3.2 7.9-7.3 7.8-14c-.1-4.6-1.8-7.8-7.7-11.6c-6.8-4.3-16.5-7.4-29-11.2l-1.6-.5s0 0 0 0c-11-3.3-24.3-7.3-34.8-13.7c-12-7.2-22.6-18.9-22.7-37.3c-.1-19.4 10.8-32.8 23.8-40.5c7.5-4.4 15.8-7.2 24.1-8.7l0-17.3c0-8.8 7.2-16 16-16z" />
                </svg>
              </div>

              <h1 className="md:text-4xl text-[30px] font-bold mb-[18px] tracking-[-1px] bg-gradient-to-br from-white to-slate-600 bg-clip-text text-transparent">
                InvoiceProquantic
              </h1>

              <p className="text-lg font-normal opacity-90 leading-relaxed md:mb-[50px] mb-6 max-w-[320px] mx-auto">
                Join our platform and streamline your billing process today
              </p>

              <ul className="list-none text-center p-0 max-w-[350px] w-full">
                <li className="md:py-4 py-3 flex justify-center items-center text-base opacity-90 transition-all duration-300 relative hover:opacity-100 hover:translate-x-[5px]">
                  <i className="hidden text-center fas fa-rocket text-slate-500 mr-[18px] w-6 text-lg transition-all duration-300"></i>
                  Set up your account in minutes
                </li>
                <li className="md:py-4 py-3 flex justify-center items-center text-base opacity-90 transition-all duration-300 relative hover:opacity-100 hover:translate-x-[5px]">
                  <i className="hidden text-center fas fa-chart-line text-slate-500 mr-[18px] w-6 text-lg transition-all duration-300"></i>
                  Track payments in real-time
                </li>
                <li className="md:py-4 py-3 flex justify-center items-center text-base opacity-90 transition-all duration-300 relative hover:opacity-100 hover:translate-x-[5px]">
                  <i className="hidden text-center fas fa-shield-alt text-slate-500 mr-[18px] w-6 text-lg transition-all duration-300"></i>
                  Enterprise-grade security
                </li>
                <li className="md:py-4 py-3 flex justify-center items-center text-base opacity-90 transition-all duration-300 relative hover:opacity-100 hover:translate-x-[5px]">
                  <i className="hidden text-center fas fa-globe text-slate-500 mr-[18px] w-6 text-lg transition-all duration-300"></i>
                  Multi-currency support
                </li>
                <li className="md:py-4 py-3 flex justify-center items-center text-base opacity-90 transition-all duration-300 relative hover:opacity-100 hover:translate-x-[5px]">
                  <i className="hidden text-center fas fa-mobile-alt text-slate-500 mr-[18px] w-6 text-lg transition-all duration-300"></i>
                  Responsive mobile design
                </li>
              </ul>
            </div>
          </div>

          {/* Right Side - Signup Form */}
          <div className="flex-[1.2] md:p-[40px] p-8 flex flex-col bg-[#faf8f4] relative overflow-y-auto">
            <div className="absolute top-0 right-0 w-[100px] h-[100px] bg-[radial-gradient(circle,#6B7C93_0%,transparent_70%)] opacity-10 rounded-full"></div>

            <div className="text-center md:mb-5 mb-4" style={{ animation: "slideInRight 0.8s ease-out 0.4s both" }}>
              <h2 className="text-[25px] mt-2 md:text-[32px] font-bold text-[#2c2419] mb-3 tracking-[-0.5px]">
                Create Your Account
              </h2>
              <p className="text-[#5d5448] text-[17px] font-normal mt-1">
                Sign up for InvoiceProquantic and start managing your invoices
              </p>
            </div>

            {error && (
              <div className="bg-[#fdf2f2] text-[#e74c3c] p-4 rounded-2xl mb-6 border-l-4 border-[#e74c3c] text-sm" style={{ animation: "slideInRight 0.8s ease-out 0.3s both" }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid sm:grid-cols-2 gap-4" style={{ animation: "slideInRight 0.8s ease-out 0.5s both" }}>
                <div className="relative">
                  <label className="block mb-[10px] text-[#2c2419] font-semibold text-[15px]" htmlFor="name">
                    First Name
                    <span className="text-red-700">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full py-4 px-5 border-2 ${validationErrors.name
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec] bg-white"
                      } rounded-2xl text-base font-normal transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] text-[#2c2419] focus:outline-none focus:border-[#6B7C93] focus:shadow-[0_0_0_4px_rgba(201,168,118,0.1),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px]`}
                    placeholder="Enter your first name"
                  />
                  {validationErrors.name && (
                    <div className="text-[#e74c3c] text-[13px] mt-[6px] font-medium">
                      {validationErrors.name}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className="block mb-[10px] text-[#2c2419] font-semibold text-[15px]" htmlFor="surname">
                    Last Name
                    <span className="text-red-700">*</span>
                  </label>
                  <input
                    type="text"
                    id="surname"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    className={`w-full py-4 px-5 border-2 ${validationErrors.surname
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec] bg-white"
                      } rounded-2xl text-base font-normal transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] text-[#2c2419] focus:outline-none focus:border-[#6B7C93] focus:shadow-[0_0_0_4px_rgba(201,168,118,0.1),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px]`}
                    placeholder="Enter your last name"
                  />
                  {validationErrors.surname && (
                    <div className="text-[#e74c3c] text-[13px] mt-[6px] font-medium">
                      {validationErrors.surname}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className="block mb-[10px] text-[#2c2419] font-semibold text-[15px]" htmlFor="tenantName">
                    Company Name
                    <span className="text-red-700">*</span>
                  </label>
                  <input
                    type="text"
                    id="tenantName"
                    name="tenantName"
                    value={formData.tenantName}
                    onChange={handleChange}
                    className={`w-full py-4 px-5 border-2 ${validationErrors.tenantName
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec] bg-white"
                      } rounded-2xl text-base font-normal transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] text-[#2c2419] focus:outline-none focus:border-[#6B7C93] focus:shadow-[0_0_0_4px_rgba(201,168,118,0.1),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px]`}
                    placeholder="Enter your company name"
                  />
                  {validationErrors.tenantName && (
                    <div className="text-[#e74c3c] text-[13px] mt-[6px] font-medium">
                      {validationErrors.tenantName}
                    </div>
                  )}
                  <div className="mt-[6px] text-[13px] text-[#8b7d6b]">
                    Your company or organization name
                  </div>
                </div>

                <div className="relative">
                  <label className="block mb-[10px] text-[#2c2419] font-semibold text-[15px]" htmlFor="tenancyName">
                    Company Display Name
                    <span className="text-red-700">*</span>
                  </label>
                  <input
                    type="text"
                    id="tenancyName"
                    name="tenancyName"
                    value={formData.tenancyName}
                    onChange={handleChange}
                    className={`w-full py-4 px-5 border-2 ${validationErrors.tenancyName
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec] bg-white"
                      } rounded-2xl text-base font-normal transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] text-[#2c2419] focus:outline-none focus:border-[#6B7C93] focus:shadow-[0_0_0_4px_rgba(201,168,118,0.1),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px]`}
                    placeholder="Enter company display name"
                  />
                  {validationErrors.tenancyName && (
                    <div className="text-[#e74c3c] text-[13px] mt-[6px] font-medium">
                      {validationErrors.tenancyName}
                    </div>
                  )}
                  <div className="mt-[6px] text-[13px] text-[#8b7d6b]">
                    The display name of your company (e.g., 'MyCompany')
                  </div>
                </div>

                <div className="relative">
                  <label className="block mb-[10px] text-[#2c2419] font-semibold text-[15px]" htmlFor="userName">
                    Username
                    <span className="text-red-700">*</span>
                  </label>
                  <input
                    type="text"
                    id="userName"
                    name="userName"
                    value={formData.userName}
                    onChange={handleChange}
                    className={`w-full py-4 px-5 border-2 ${validationErrors.userName
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec] bg-white"
                      } rounded-2xl text-base font-normal transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] text-[#2c2419] focus:outline-none focus:border-[#6B7C93] focus:shadow-[0_0_0_4px_rgba(201,168,118,0.1),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px]`}
                    placeholder="Choose a username"
                  />
                  {validationErrors.userName && (
                    <div className="text-[#e74c3c] text-[13px] mt-[6px] font-medium">
                      {validationErrors.userName}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className="block mb-[10px] text-[#2c2419] font-semibold text-[15px]" htmlFor="email">
                    Email
                    <span className="text-red-700">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full py-4 px-5 border-2 ${validationErrors.email
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec] bg-white"
                      } rounded-2xl text-base font-normal transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] text-[#2c2419] focus:outline-none focus:border-[#6B7C93] focus:shadow-[0_0_0_4px_rgba(201,168,118,0.1),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px]`}
                    placeholder="Enter your email"
                  />
                  {validationErrors.email && (
                    <div className="text-[#e74c3c] text-[13px] mt-[6px] font-medium">
                      {validationErrors.email}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className="block mb-[10px] text-[#2c2419] font-semibold text-[15px]" htmlFor="phoneNumber">
                    Phone Number <span className="text-sm">(Optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className={`w-full py-4 px-5 border-2 ${validationErrors.phoneNumber
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec] bg-white"
                      } rounded-2xl text-base font-normal transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] text-[#2c2419] focus:outline-none focus:border-[#6B7C93] focus:shadow-[0_0_0_4px_rgba(201,168,118,0.1),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px]`}
                    placeholder="Enter your phone number"
                  />
                  {validationErrors.phoneNumber && (
                    <div className="text-[#e74c3c] text-[13px] mt-[6px] font-medium">
                      {validationErrors.phoneNumber}
                    </div>
                  )}
                  <div className="mt-[6px] text-[13px] text-[#8b7d6b]">
                    We'll only use this for account verification
                  </div>
                </div>

                <div className="relative">
                  <label className="block mb-[10px] text-[#2c2419] font-semibold text-[15px]" htmlFor="password">
                    Password
                    <span className="text-red-700">*</span>
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full py-4 px-5 border-2 ${validationErrors.password
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec] bg-white"
                      } rounded-2xl text-base font-normal transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] text-[#2c2419] focus:outline-none focus:border-[#6B7C93] focus:shadow-[0_0_0_4px_rgba(201,168,118,0.1),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px]`}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    className="absolute right-[8px] top-[37px] text-base bg-none border-none text-[#8b7d6b] cursor-pointer p-2 rounded-lg transition-all duration-300 hover:text-[#6B7C93] bg-transparent"
                    onClick={togglePasswordVisibility}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                  {validationErrors.password && (
                    <div className="text-[#e74c3c] text-[13px] mt-[6px] font-medium">
                      {validationErrors.password}
                    </div>
                  )}
                  <div className="mt-[6px] text-[13px] text-[#8b7d6b]">
                    Password should be at least 8 characters
                  </div>
                </div>

                <div className="mb-[16px] relative">
                  <label className="block mb-[10px] text-[#2c2419] font-semibold text-[15px]" htmlFor="confirmPassword">
                    Confirm Password
                    <span className="text-red-700">*</span>
                  </label>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    className={`w-full py-4 px-5 border-2 ${validationErrors.confirmPassword
                      ? "border-[#e74c3c] bg-[#fdf2f2]"
                      : "border-[#f5f2ec] bg-white"
                      } rounded-2xl text-base font-normal transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] text-[#2c2419] focus:outline-none focus:border-[#6B7C93] focus:shadow-[0_0_0_4px_rgba(201,168,118,0.1),0_8px_16px_rgba(169,144,128,0.1)] focus:-translate-y-[2px]`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="absolute right-[8px] top-[37px] text-base bg-none border-none text-[#8b7d6b] cursor-pointer p-2 rounded-lg transition-all duration-300 hover:text-[#6B7C93] bg-transparent"
                    onClick={toggleConfirmPasswordVisibility}
                  >
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </button>
                  {validationErrors.confirmPassword && (
                    <div className="text-[#e74c3c] text-[13px] mt-[6px] font-medium">
                      {validationErrors.confirmPassword}
                    </div>
                  )}
                </div>
              </div>

              {/* <button
                type="submit"
                className={`w-full py-[18px] bg-gradient-to-br from-[#6B7C93] to-[#2C3E50] text-white border-none rounded-2xl text-[17px] font-semibold cursor-pointer transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] mt-[0px] mb-[16px] relative overflow-hidden animate-[slideInRight_0.8s_ease-out_1s_both] hover:-translate-y-[3px] hover:shadow-[0_16px_32px_rgba(201,168,118,0.3)] ${isLoading ? "pointer-events-none" : ""
                  }`}
                disabled={isLoading}
              >
                {isLoading ? "Creating Account..." : "Create Account"}
              </button> */}
              <button
                type="submit"
                disabled={
                  isLoading ||
                  checkingAvailability ||
                  !tenantNameAvailable ||
                  !tenancyNameAvailable
                }
                className={`w-full py-[18px] bg-gradient-to-br from-[#6B7C93] to-[#2C3E50] text-white border-none rounded-2xl text-[17px] font-semibold cursor-pointer transition-all duration-400 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] mt-[0px] mb-[16px] relative overflow-hidden animate-[slideInRight_0.8s_ease-out_1s_both] hover:-translate-y-[3px] hover:shadow-[0_16px_32px_rgba(201,168,118,0.3)] ${isLoading ? "pointer-events-none" : ""
                  }`}
              >
                {isLoading ? "Creating Account..." : checkingAvailability ? "Checking availability..." : "Create Account"}
              </button>

              <div className="text-center text-[#8b7d6b] text-[15px] font-medium" style={{ animation: "slideInRight 0.8s ease-out 1.1s both" }}>
                Already have an account?{" "}
                <Link to="/login" className="text-[#6B7C93] no-underline font-semibold transition-all duration-300 relative hover:text-[#2C3E50]">
                  Sign In
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;